import React, { useEffect, useState } from 'react';
import { Download, Share, Plus, X, Apple, Smartphone, AlertTriangle } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { usePWAInstall } from '../lib/usePWAInstall';
import { isDriverHost } from '../lib/pwaHost';

/**
 * PWAInstallPrompt — compact bottom-sheet install nudge.
 *
 * Design goals after redesign:
 *  - Bottom-sheet layout on mobile (≤640px) so it never blocks the page above.
 *  - Only the install steps relevant to the user's platform — iOS shows iOS
 *    only, Android+others show the native prompt button OR Chrome menu steps.
 *  - No duplicate headlines / description / icon blocks — total height roughly
 *    one third of viewport instead of the previous ~70%.
 *  - Context-aware for driver portal (emerald) vs storefront (blue), with an
 *    inline yellow warning on iOS+driver about the title/scope gotcha.
 */
const PWAInstallPrompt = ({ open: openProp, onClose }) => {
  const { canInstall, installed, platform, install } = usePWAInstall();
  const location = useLocation();
  // Driver context is true on the dedicated driver subdomain OR on the
  // legacy /driver/* paths served from the storefront origin.
  const driverHost = isDriverHost();
  const isDriver = driverHost || location.pathname.startsWith('/driver');
  const [open, setOpen] = useState(false);
  // Did the user previously install the storefront on this device? We track
  // this via localStorage flag set by usePWAInstall on 'appinstalled'. When
  // true AND we're on /driver, the Chrome install prompt won't fire — we
  // show explicit "uninstall storefront first" instructions instead.
  //
  // CRITICAL: when we're on the dedicated driver subdomain, there is no
  // conflict possible (different origin → different localStorage too, so
  // this flag will be absent anyway). Force-disable the warning to be safe.
  const hasStorefrontInstalled =
    !driverHost &&
    typeof window !== 'undefined' &&
    localStorage.getItem('mr_pwa_installed') === '1';

  const controlled = typeof openProp === 'boolean';

  // Desktop PCs (mouse + fine pointer + wide viewport) don't benefit from
  // an "Add to Home Screen" nudge — Chrome already offers an install icon
  // in the address bar and the home-screen metaphor is mobile-first.
  // We only auto-open this sheet on touch / small-viewport devices.
  const isDesktop = () => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(
      '(min-width: 1024px) and (hover: hover) and (pointer: fine)'
    ).matches;
  };

  useEffect(() => {
    if (controlled) {
      setOpen(openProp);
      return;
    }
    if (installed) return;
    if (isDesktop()) return;
    const dismissed = sessionStorage.getItem('mr_pwa_dismissed') === '1';
    if (dismissed) return;
    if (platform === 'ios' || canInstall) {
      setOpen(true);
    } else {
      const t = setTimeout(() => {
        if (!installed) setOpen(true);
      }, 1500);
      return () => clearTimeout(t);
    }
  }, [controlled, openProp, installed, platform, canInstall]);

  const close = () => {
    setOpen(false);
    try { sessionStorage.setItem('mr_pwa_dismissed', '1'); } catch { /* ignore */ }
    if (controlled && onClose) onClose();
  };

  const handleInstall = async () => {
    const choice = await install();
    if (choice.outcome === 'accepted') close();
  };

  if (!open || installed) return null;

  const accent = isDriver ? 'emerald' : 'sky';
  const accentBtn = isDriver
    ? 'bg-emerald-600 hover:bg-emerald-700'
    : 'bg-[#0078D4] hover:bg-[#005A9E]';
  const accentText = isDriver ? 'text-emerald-700' : 'text-[#0078D4]';
  const accentBg = isDriver ? 'bg-emerald-50' : 'bg-[#DEECF9]';

  // Per-path install prompt — drivers installing from /driver/* get
  // the driver-branded sheet and end up with a separate "MR Driver"
  // icon on iOS. On Chrome/Android, scope `/` and `/driver` overlap
  // so only the first-installed app sticks; we let the install
  // attempt proceed regardless (Chrome just silently no-ops if the
  // user already has the other one). StandaloneRoleRouter handles
  // post-launch routing for both cases.
  const appName = isDriver ? 'Driver Portal' : 'MR E-Store';
  const tagline = isDriver
    ? 'Lock-screen dispatch alerts + one-tap delivery queue.'
    : 'Faster loads, signed-in shortcut on your home screen.';

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:px-4 pointer-events-none"
      data-testid="pwa-install-prompt"
    >
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm pointer-events-auto"
        onClick={close}
        data-testid="pwa-install-backdrop"
      />

      <div
        className="relative w-full sm:max-w-sm bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl border-t sm:border border-slate-200 pointer-events-auto animate-in slide-in-from-bottom-4 fade-in duration-300"
        role="dialog"
        aria-labelledby="pwa-install-title"
      >
        {/* drag handle (mobile) */}
        <div className="sm:hidden flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-slate-300" />
        </div>

        <div className="px-5 pt-3 sm:pt-5 pb-1 flex items-start gap-3">
          <div className={`w-11 h-11 rounded-xl ${accentBg} ${accentText} flex items-center justify-center flex-shrink-0`}>
            {isDriver ? <Smartphone className="w-5 h-5" /> : <Download className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <h2 id="pwa-install-title" className="font-display text-lg text-[#0F172A] leading-tight">
              Install the {appName}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{tagline}</p>
          </div>
          <button
            onClick={close}
            className="text-slate-400 hover:text-slate-700 -mr-1 p-1 rounded-full"
            aria-label="Close"
            data-testid="pwa-install-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pb-5 pt-3">
          {/*
            Chrome/Android scope-conflict warning.

            Chrome enforces one PWA per overlapping scope per origin.
            The storefront's scope is `/` (claims `/driver` too), so
            once the storefront is installed, Chrome silently rejects
            the driver install with "App already installed" — even
            though it's the WRONG app from the driver's perspective.
            iOS Safari doesn't have this rule (each AHS shortcut is
            independent), so we only show this on Android-Chrome
            when storefront is installed AND the install prompt is
            disabled AND we're on the driver portal.
          */}
          {isDriver && !canInstall && platform !== 'ios' && hasStorefrontInstalled && (
            <div
              className="mb-3 rounded-lg bg-amber-50 border border-amber-300 p-3"
              data-testid="pwa-install-conflict"
            >
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900 font-bold leading-snug">
                  Chrome on Android allows only one MR app at a time
                </p>
              </div>
              <p className="text-[11px] text-amber-900 mb-2 leading-snug">
                The orange <b>MR E-Store</b> icon is already on your home screen and Chrome treats this whole site as one app. To put the yellow <b>Driver Portal</b> icon on your home screen, you have two options:
              </p>
              <ol className="space-y-1.5 text-[11px] text-amber-900 list-decimal list-inside leading-snug">
                <li><b>Drivers-only device</b>: Long-press the orange MR icon → Uninstall → refresh this page → install the yellow Driver icon.</li>
                <li><b>Keep both</b>: Just sign in here in Chrome. Your driver dispatch dashboard works the same in the browser tab — bookmark it for one-tap access.</li>
              </ol>
            </div>
          )}

          {platform === 'ios' ? (
            <IOSSteps accentBg={accentBg} accentText={accentText} />
          ) : canInstall ? (
            <Button
              onClick={handleInstall}
              className={`w-full h-11 rounded-full ${accentBtn} text-white font-semibold`}
              data-testid="pwa-install-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Install now
            </Button>
          ) : (
            <AndroidSteps accentBg={accentBg} accentText={accentText} />
          )}

          <button
            onClick={close}
            className="w-full mt-2 text-xs text-slate-500 hover:text-slate-700 py-2"
            data-testid="pwa-install-later"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

const Step = ({ n, accentBg, accentText, children }) => (
  <li className="flex items-start gap-2.5">
    <span className={`w-5 h-5 rounded-full ${accentBg} ${accentText} font-bold text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5`}>
      {n}
    </span>
    <span className="leading-snug">{children}</span>
  </li>
);

const IOSSteps = ({ accentBg, accentText }) => (
  <div className="space-y-1">
    <div className={`text-[10px] font-bold uppercase tracking-wider ${accentText} flex items-center gap-1 mb-1.5`}>
      <Apple className="w-3 h-3" /> iPhone / iPad
    </div>
    <ol className="space-y-2 text-xs text-slate-700">
      <Step n={1} accentBg={accentBg} accentText={accentText}>
        Tap <Share className={`w-3.5 h-3.5 inline-block ${accentText} mx-0.5 align-text-bottom`} /> <b>Share</b> in Safari.
      </Step>
      <Step n={2} accentBg={accentBg} accentText={accentText}>
        Tap <Plus className={`w-3.5 h-3.5 inline-block ${accentText} mx-0.5 align-text-bottom`} /> <b>Add to Home Screen</b>.
      </Step>
      <Step n={3} accentBg={accentBg} accentText={accentText}>
        Tap <b>Add</b> (top-right), then open the new icon.
      </Step>
    </ol>
  </div>
);

const AndroidSteps = ({ accentBg, accentText }) => (
  <div className="space-y-1">
    <div className={`text-[10px] font-bold uppercase tracking-wider ${accentText} mb-1.5`}>
      Android / Chrome
    </div>
    <ol className="space-y-2 text-xs text-slate-700">
      <Step n={1} accentBg={accentBg} accentText={accentText}>
        Open the browser menu (⋮ top-right).
      </Step>
      <Step n={2} accentBg={accentBg} accentText={accentText}>
        Tap <b>Install app</b> or <b>Add to Home screen</b>.
      </Step>
      <Step n={3} accentBg={accentBg} accentText={accentText}>
        Confirm — the new icon appears on your Home screen.
      </Step>
    </ol>
  </div>
);

export default PWAInstallPrompt;
